import type { MenuItem, Nullable } from '@accesso/cc-std-lib';
import type { Params } from '@angular/router';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { isNonEmptyString, stringToSlug } from '@accesso/cc-std-lib';
import { CategoryModel, LocaleModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';
import { BreadcrumbsComponent as UdBreadcrumbsComponent } from '@accesso/ng-uds/breadcrumbs';

import { QUERY_PARAM_PREVIOUS_PAGE_TYPE, QUERY_PARAM_PREVIOUS_PAGE_VALUE } from '../../../core/enums/query-params';
import { getPageNameByValue } from '../../../core/utilities/merchant';
import { MerchantStore } from '../../merchant/merchant.store';

export const PREV_PAGE_TYPE_CATEGORY = 'category';
export const PREV_PAGE_TYPE_TEMPLATE = 'page';

@Component({
	selector: 'app-breadcrumbs',
	imports: [UdBreadcrumbsComponent],
	template: `<ud-breadcrumbs [items]="model()" />`,
	styleUrl: './breadcrumbs.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		// We must skip hydration on this as it causes weird rendering artifacts where the previous navigation item is pushed to the front, then home page, then the rest of the items passed in.
		ngSkipHydration: 'true',
	},
})
export class BreadcrumbsComponent {
	private readonly jsonApi = inject(JsonApiService);
	private readonly navigation = inject(MerchantStore).merchant()?.navigation() ?? [];
	private readonly commonLocales = this.jsonApi.findOne(LocaleModel, 'common');
	private readonly queryParams = toSignal(inject(ActivatedRoute).queryParams, { initialValue: {} as Params });

	items = input.required<MenuItem[]>();

	// Find out if we have a prev page from the query parameters
	private hasPrevPage = computed(() => {
		const queryParams = this.queryParams();
		const prevPage = queryParams[QUERY_PARAM_PREVIOUS_PAGE_VALUE];
		const prevPageType = queryParams[QUERY_PARAM_PREVIOUS_PAGE_TYPE];

		return isNonEmptyString(prevPage) && isNonEmptyString(prevPageType);
	});

	// Returns a category model if the previous page was a category
	private prevPageModel = computed<Nullable<CategoryModel>>(() => {
		const query = this.queryParams();
		if (!this.hasPrevPage()) return null;

		const type = query[QUERY_PARAM_PREVIOUS_PAGE_TYPE];
		const value = query[QUERY_PARAM_PREVIOUS_PAGE_VALUE];

		return type === PREV_PAGE_TYPE_CATEGORY ? this.jsonApi.findOne(CategoryModel, value) : null;
	});

	// Computes the previous page breadcrumb item if applicable
	private readonly previousPageItem = computed(() => {
		const queryParams = this.queryParams();
		const prevPageModel = this.prevPageModel();

		if (!this.hasPrevPage()) return null;

		const prevPageType = queryParams[QUERY_PARAM_PREVIOUS_PAGE_TYPE];
		const prevPageValue = queryParams[QUERY_PARAM_PREVIOUS_PAGE_VALUE];
		const path = prevPageType === PREV_PAGE_TYPE_CATEGORY ? PREV_PAGE_TYPE_CATEGORY : PREV_PAGE_TYPE_TEMPLATE;
		let label = '';

		const extraParams: (string | number)[] = [];

		if (prevPageType !== PREV_PAGE_TYPE_CATEGORY) {
			label = getPageNameByValue(prevPageValue, this.navigation);
		} else if (prevPageModel && prevPageModel.state().loaded) {
			label = prevPageModel.name();
			const slug = prevPageModel.slug();
			extraParams.push(isNonEmptyString(slug) ? slug : stringToSlug(label));
		}

		if (label) {
			return <MenuItem>{
				label,
				routerLink: ['/', path, prevPageValue, ...extraParams],
			};
		}

		return null;
	});

	// Our model for ng-uds the breadcrumbs component
	model = computed(() => {
		const { commonLocales } = this;
		const prevPage = this.previousPageItem();
		const breadcrumbs: MenuItem[] = [];

		if (commonLocales.state().loaded) {
			breadcrumbs.push(<MenuItem>{
				label: commonLocales.attributes()['homepage'] ?? 'Home',
				routerLink: ['/'],
			});
		}

		if (prevPage) {
			breadcrumbs.push(prevPage);
		}

		return breadcrumbs.concat(this.items());
	});
}
